import { forwardRef } from 'react'
import type { JSX } from 'react'
import type { HTMLAttributes, ReactNode } from 'react'

import { clsx } from 'clsx'
import { formatDistanceToNowStrict } from 'date-fns'

import { Flex, Text } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'

import { TextContent } from './CommentText'
import { EngagementAvatar } from '../../components/EngagementAvatar'
import { EngagementItem } from '../../components/EngagementItem'
import { EngagementMedia } from '../../components/EngagementMedia'
import { useCommentData } from '../context/CommentContext'
import type { CommentData } from '../fragments/commentData'

import styles from './CommentThreadContent.module.css'

export const OriginalPostCard_Channel = graphql(/* GraphQL */ `
  fragment OriginalPostCard_Channel on Channel @_unmask {
    name
    avatar
  }
`)

type OriginalPostCardChannel = FragmentOf<typeof OriginalPostCard_Channel>

type OriginalPost = NonNullable<CommentData['post']>

export const OriginalPostContainer = forwardRef<
  HTMLElement,
  HTMLAttributes<HTMLElement> & {
    as?: 'article' | 'section'
    post: OriginalPost
    children: ReactNode
  }
>(
  (
    { as: Component = 'section', className, children, post, ...props },
    ref,
  ): JSX.Element => {
    return (
      <Component
        ref={ref}
        className={clsx(styles.container, className)}
        data-post-id={post.id}
        role={Component === 'section' ? 'group' : undefined}
        {...props}
      >
        <Flex direction="column" gap="xs">
          {children}
        </Flex>
      </Component>
    )
  },
)
OriginalPostContainer.displayName = 'OriginalPostContainer'

const Header = ({
  comment,
  channel,
}: {
  comment: CommentData
  channel: OriginalPostCardChannel
}): JSX.Element => {
  const { postPublishedAt, post } = comment

  return (
    <header>
      <Flex id={`${post.id}-header`} align="center" gap="xs">
        <Text weight="bold" translate="no">
          {channel.name}
        </Text>

        {postPublishedAt && (
          <Text size="sm" color="subtle">
            {formatDistanceToNowStrict(new Date(postPublishedAt), {
              addSuffix: true,
            })}
          </Text>
        )}
      </Flex>
    </header>
  )
}

const PostContent = ({
  channel,
}: {
  channel: OriginalPostCardChannel
}): JSX.Element | null => {
  const comment = useCommentData()
  const { post } = comment

  if (!post) {
    return null
  }

  return (
    <Flex
      direction="column"
      gap="xs"
      data-testid="original-post-card"
      data-original-post-id={post.id}
    >
      <EngagementItem
        aria-labelledby={`${post.id}-header`}
        variant="root"
        data-testid="comment-item"
        avatar={
          <EngagementAvatar
            hasParent={false}
            avatar={channel.avatar}
            username={channel.name}
            showConnector={true}
          />
        }
      >
        <Header comment={comment} channel={channel} />
        <TextContent text={post.text} serviceType={post.channelService} />
        <EngagementMedia assets={post.assets} />
      </EngagementItem>
    </Flex>
  )
}

export const OriginalPostCard = ({
  channel,
}: {
  channel: OriginalPostCardChannel
}): JSX.Element => {
  const comment = useCommentData()

  return (
    <OriginalPostContainer post={comment.post}>
      <PostContent channel={channel} />
    </OriginalPostContainer>
  )
}
